import { useUserQuery } from "@/api/users-api"
import {
	ExportImageOptions,
	publishDimensionAtom,
	publishDimensionSideAtom,
	publishDimensionTypeAtom,
	publishQualityAtom,
	publishColorSpaceAtom,
	publishPixelPerInchAtom,
	publishWatermarkAtom,
	publishSelectedOptionAtom,
} from "@/core/export-image"
import { getExportResizeOptions } from "@/core/export-resize-options"
import { resetDialogs, pushDialog } from "@/store/dialogs-state"
import {
	displayGroupPhotosCountAtom,
	exportablePhotosAtom,
	metadataAtom,
	selectedExportablePhotosAtom,
	selectedPhotosCountAtom,
	selectedExportablePhotosCountAtom,
} from "@/store/photo/photos-state"
import { projectAtom } from "@/store/project/projects-state"
import { Sentry } from "@/telemetry/sentry"
import { useAtom, useAtomValue } from "jotai"
import toast from "react-hot-toast"
import { Button } from "../ui/Button"
import { ConfirmDialog } from "./ConfirmDialog"
import { ExportOptionsView } from "./ExportDialog"
import { Dialog } from "@/components/ui/Dialog"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
	collectionAtom,
	publishPhotos,
	setUpWorkspace,
	galleryUpdatesAtom,
	checkForStorageAvailability,
	galleriesQueryAtom,
	resolveGalleryId,
	selectedCollectionQueryAtom,
} from "@/store/Pixieset/store-client-gallery"
import {
	collectionIdAtom,
	galleryIdAtom,
	isPhotoStorageAvailableAtom,
} from "@/store/Pixieset/pixieset-state"
import { clearPixiesetCollectionSelection } from "@/store/Pixieset/pixieset-selection"

import { ProgressBar } from "../ui/ProgressBar"
import {
	cancelPublishFromDialog,
	publishCancelRequestedAtom,
	publishProgressAtom,
} from "@/store/editor/photo-publishing"
import { uploadMetricsAtom } from "@/store/Pixieset/ImageUploadService"
import { SelectCGCollectionDialog } from "./SelectCGCollectionDialog"
import { ExportScopeSelector } from "./ExportScopeSelector"
import { runExportPublishPreflight } from "./SubscriptionWarningDialogs"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "../ui/Select"
import { useEstimatedExportSize } from "./useEstimatedExportSize"

export const PublishDialog = () => {
	const { t } = useTranslation("components")
	const [collection, setCollection] = useAtom(collectionAtom)
	const [selectedOption, setSelectedOption] = useAtom(publishSelectedOptionAtom)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const isSubmittingRef = useRef(false)

	const project = useAtomValue(projectAtom)
	const photos = useAtomValue(exportablePhotosAtom)
	const metadata = useAtomValue(metadataAtom)
	const selectedPhotos = useAtomValue(selectedExportablePhotosAtom)
	const selectedCount = useAtomValue(selectedPhotosCountAtom)
	const selectedExportableCount = useAtomValue(selectedExportablePhotosCountAtom)
	const photoCount = selectedOption == "selected" ? selectedExportableCount : photos.length
	const quality = useAtomValue(publishQualityAtom)
	const colorSpace = useAtomValue(publishColorSpaceAtom)
	const pixelPerInch = useAtomValue(publishPixelPerInchAtom)
	const dimensionType = useAtomValue(publishDimensionTypeAtom)
	const dimensionSide = useAtomValue(publishDimensionSideAtom)
	const dimension = useAtomValue(publishDimensionAtom)
	const [hasStorageToPublish, setHasStorageToPublish] = useAtom(isPhotoStorageAvailableAtom)
	const userQuery = useUserQuery(false)
	const selectedCollectionId = useAtomValue(collectionIdAtom)
	const selectedCollectionState = useAtomValue(selectedCollectionQueryAtom)
	const {
		photosToDelete,
		photosToUpdate,
		photosToUpload,
		photosToPublish,
		publishFilenames,
		hasChanges,
		isLoading,
	} = useAtomValue(galleryUpdatesAtom)
	const [galleryId, setGalleryId] = useAtom(galleryIdAtom)
	const galleriesState = useAtomValue(galleriesQueryAtom)
	const galleries = useMemo(() => galleriesState.data ?? [], [galleriesState.data])
	const selectedGallery = galleries.find((gallery) => gallery.id === galleryId)

	const [{ processing: processingCount }] = useAtom(displayGroupPhotosCountAtom)
	const watermarkId = useAtomValue(publishWatermarkAtom)

	const isProcessing = processingCount > 0
	const hasTooSmallCustomDimension = dimensionType === "Custom" && dimension < 1
	const photosInPublishScope = selectedOption === "selected" ? selectedPhotos : photos
	const estimatedPublishSize = useEstimatedExportSize({
		projectId: project?.id,
		photos: photosInPublishScope,
		metadata,
		format: "jpg",
		quality,
		colorSpace,
		dimensionType,
		dimensionSide,
		dimension,
	})
	const publishSizeError = hasTooSmallCustomDimension
		? t("publishDialog.publishSizeError")
		: estimatedPublishSize.hasUnsupportedCustomDimensions
			? t("publishDialog.publishSizeError2")
			: null

	const confirmPublish = async () => {
		if (isSubmittingRef.current) {
			return
		}
		if (collection === null) {
			return
		}
		if (publishSizeError !== null) {
			return
		}

		isSubmittingRef.current = true
		setIsSubmitting(true)

		try {
			const canProceed = await runExportPublishPreflight({
				photoCount,
				origin: "publish menu",
				refetchUser: () => userQuery.refetch(),
				skipCreditCheck: project?.isSampleProject === true,
			})
			if (!canProceed) {
				isSubmittingRef.current = false
				setIsSubmitting(false)
				return
			}

			if (photosToPublish.length > 0 || photosToDelete.length > 0) {
				const options = getExportOptions()
				const didStart = await publishPhotos(
					photosToPublish,
					options,
					[...photosToDelete],
					publishFilenames,
				)
				if (!didStart) {
					isSubmittingRef.current = false
					setIsSubmitting(false)
				}
			}
		} catch (e) {
			isSubmittingRef.current = false
			setIsSubmitting(false)
			toast.error(t("publishDialog.failedToPublishPleaseTry"))
			console.error("Failed to publish photos", e)
			Sentry.captureException(e)
		}
	}

	const getExportOptions = () => {
		const options: ExportImageOptions = {
			projectId: project?.id,
			type: "jpg",
			filenameType: "original",
			quality,
			colorSpace,
			ppi: pixelPerInch,
			format: "yuv",
			watermark: watermarkId,
			...getExportResizeOptions(dimensionType, dimensionSide, dimension),
		}

		return options
	}

	useEffect(() => {
		void setUpWorkspace()
	}, [])

	useEffect(() => {
		if (
			selectedCollectionState.isInitialLoading ||
			selectedCollectionState.isFetching ||
			selectedCollectionState.isError ||
			!selectedCollectionId
		) {
			return
		}

		if (selectedCollectionState.data === null) {
			clearPixiesetCollectionSelection()
		}
	}, [selectedCollectionId, selectedCollectionState])

	useEffect(() => {
		if (!selectedCollectionId || galleriesState.isLoading || galleriesState.isError) {
			return
		}

		const nextGalleryId = resolveGalleryId(galleries, galleryId)
		if (nextGalleryId !== galleryId) {
			setGalleryId(nextGalleryId)
			setHasStorageToPublish(undefined)
		}
	}, [
		galleries,
		galleriesState.isError,
		galleriesState.isLoading,
		galleryId,
		selectedCollectionId,
		setGalleryId,
		setHasStorageToPublish,
	])

	useEffect(() => {
		if (!collection || !galleryId || hasStorageToPublish !== undefined) {
			return
		}

		checkForStorageAvailability()
	}, [collection, galleryId, hasStorageToPublish])

	const selectGallery = (nextGalleryId: string) => {
		if (nextGalleryId === galleryId) {
			return
		}
		setGalleryId(nextGalleryId)
		setHasStorageToPublish(undefined)
	}

	return (
		<Dialog trackingViewName="publish_dialog" contentSize="sm-h-auto" disableClose>
			<div className="flex w-full flex-col items-start justify-start space-y-4 text-sm">
				<div className="text-start text-md font-bold">{t("publishDialog.title")}</div>
				<ExportOptionsView type="publish" />

				<div className="flex h-px w-full bg-base-700"></div>
				<ExportScopeSelector
					groupName="publish-scope"
					selectedOption={selectedOption}
					setSelectedOption={setSelectedOption}
					selectedCount={selectedCount}
					selectedExportableCount={selectedExportableCount}
					totalCount={photos.length}
					isProcessing={isProcessing}
				/>
				<div className="text-sm text-base-400">
					{t("publishDialog.estimatedPublishSize")}&nbsp;
					<span className="text-base-50">{estimatedPublishSize.text}</span>
				</div>
				{publishSizeError && (
					<div className="text-start text-xs text-red-400">{publishSizeError}</div>
				)}

				<div className="flex h-px w-full bg-base-700"></div>
				<div className="flex w-full flex-col space-y-0">
					<div className="flex w-full flex-row items-center justify-between">
						{collection && (
							<div className="flex flex-row space-x-3 font-medium">
								<div className="text-base-400">
									{t("publishDialog.selectedCollection")}
								</div>
								<div className="text-base-200">{collection.name}</div>
							</div>
						)}
						<Button
							trackingLabel="select_collection"
							size="sm"
							onClick={() => {
								pushDialog(SelectCGCollectionDialog, {
									trackingViewName: "select_cg_collection_dialog",
									onSelect: (collection) => {
										if (collection) {
											// Set the selected collection in the atom
											setCollection(collection)
										}
									},
								})
							}}
						>
							{t("publishDialog.selectCollection")}
						</Button>
					</div>
					{collection && (
						<div className="flex w-full flex-row items-center justify-between space-x-3 font-medium">
							<div className="w-fit whitespace-nowrap text-base-400">
								{t("publishDialog.selectedSet")}
							</div>
							{galleries.length > 0 ? (
								<Select
									trackingContext="publish_dialog"
									trackingLabel="gallery_set"
									aria-label={t("publishDialog.ariaSelectSet")}
									value={galleryId ?? undefined}
									onValueChange={selectGallery}
								>
									<SelectTrigger
										className="w-auto"
										isLoading={galleriesState.isLoading}
									>
										<div className="mr-2 w-fit text-base-200">
											{selectedGallery?.name}
										</div>
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{galleries.map((gallery) => (
												<SelectItem key={gallery.id} value={gallery.id}>
													{gallery.name}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							) : (
								<div className="text-base-400">
									{galleriesState.isError
										? t("publishDialog.failedToLoadSets")
										: galleriesState.isLoading
											? t("publishDialog.loadingSets")
											: t("publishDialog.noSetsAvailable")}
								</div>
							)}
						</div>
					)}

					{galleryId && !isLoading && (
						<div className="flex w-full flex-row items-start space-x-3">
							<div className="font-medium text-base-400">
								{t("publishDialog.changesToPublish")}
							</div>
							<div className="flex flex-col space-y-2">
								{photosToUpload.length > 0 && (
									<div className="text-base-200">
										{t("publishDialog.photosWillBeAdded", {
											count: photosToUpload.length,
										})}
									</div>
								)}
								{photosToUpdate.length > 0 && (
									<div className="text-base-200">
										{t("publishDialog.photosWillBeUpdated", {
											count: photosToUpdate.length,
										})}
									</div>
								)}
								{photosToDelete.length > 0 && (
									<div className="text-base-200">
										{t("publishDialog.photosWillBeDeleted", {
											count: photosToDelete.length,
										})}
									</div>
								)}
								{hasChanges === false && (
									<div className="text-base-200">
										{t("publishDialog.noChangesDetected")}
									</div>
								)}
							</div>
						</div>
					)}
				</div>

				{hasStorageToPublish === false && (
					<div className="text-xs text-base-400">
						{t("publishDialog.insufficientStorage")}
					</div>
				)}

				<div className="flex w-full flex-1 flex-row items-center justify-end space-x-2">
					<Button
						trackingLabel="cancel_publish"
						aria-label={t("publishDialog.ariaCancelPublish")}
						onClick={() => {
							resetDialogs()
						}}
					>
						{t("common:action.cancel")}
					</Button>
					<Button
						trackingLabel="confirm_publish"
						aria-label={t("publishDialog.ariaConfirmPublish")}
						onClick={() => {
							if (photosToDelete.length > 0) {
								pushDialog(ConfirmDialog, {
									title: t("publishDialog.deleteConfirmTitle"),
									message: t("publishDialog.deleteConfirmMessage", {
										count: photosToDelete.length,
									}),
									primary: {
										title: t("publishDialog.deleteAndPublish"),
										isDestructive: true,
										handler: () => {
											void confirmPublish()
										},
									},
									secondary: { title: t("common:action.cancel") },
									trackingViewName: "delete_photos_before_publish_confirmation",
								})
							} else {
								void confirmPublish()
							}
						}}
						variant="primary"
						disabled={
							photoCount < 1 ||
							publishSizeError !== null ||
							!hasChanges ||
							!galleryId ||
							!hasStorageToPublish ||
							isSubmitting ||
							(isProcessing && selectedOption === "all")
						}
					>
						{t("publishDialog.publish")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}

export const PublishLoadingDialog = () => {
	const { t } = useTranslation("components")
	const isCanceling = useAtomValue(publishCancelRequestedAtom)
	const uploadSpeed = useAtomValue(uploadMetricsAtom).emaSpeed
	const { uploads, totalToUpload, totalProgress, total, timeRemaining } =
		useAtomValue(publishProgressAtom)

	const cancel = () => {
		//TODO: pause export while user confirms
		pushDialog(ConfirmDialog, {
			title: t("publishDialog.cancelPublishTitle"),
			message: t("publishDialog.cancelPublishMessage"),
			primary: {
				title: t("publishDialog.cancelPublishPrimary"),
				isDestructive: true,
				handler: cancelPublishFromDialog,
			},
			secondary: { title: t("common:action.back") },
			trackingViewName: "cancel_publish_confirmation",
		})
	}

	const toBitrate = (bytes: number) => {
		const bits = bytes * 8
		if (bits >= 1000000000) {
			return `${(bits / 1000000000).toFixed(2)} GBit/s`
		}

		if (bits >= 1000000) {
			return `${(bits / 1000000).toFixed(2)} MBit/s`
		}

		if (bits >= 1000) {
			return `${(bits / 1000).toFixed(2)} kbit/s`
		}

		return `${bits.toFixed(2)} bit/s`
	}

	const formatTimeRemaining = (seconds: number) => {
		if (seconds < 60) {
			return t("publishDialog.seconds", { count: Math.ceil(seconds) })
		}
		const hours = Math.floor(seconds / 3600)
		const minutes = Math.floor((seconds % 3600) / 60)
		const remainingSeconds = Math.ceil(seconds % 60)
		const minutesStr = t("publishDialog.minutes", { count: minutes })
		const secondsStr = t("publishDialog.seconds", { count: remainingSeconds })
		if (hours > 0) {
			const hoursStr = t("publishDialog.hours", { count: hours })
			return `${hoursStr} ${minutesStr} ${secondsStr}`
		}
		return `${minutesStr} ${secondsStr}`
	}

	const message = () => {
		if (isCanceling) {
			return t("publishDialog.cancellingPublish")
		}

		if (uploadSpeed === 0) {
			return t("publishDialog.preparingUpload")
		} else if (isNaN(timeRemaining) || timeRemaining === Infinity) {
			return t("publishDialog.uploadingProgress", {
				uploads,
				totalToUpload,
				bitrate: toBitrate(uploadSpeed),
			})
		} else {
			return t("publishDialog.uploadingProgressTime", {
				uploads,
				totalToUpload,
				bitrate: toBitrate(uploadSpeed),
				timeRemaining: formatTimeRemaining(timeRemaining),
			})
		}
	}

	return (
		<Dialog trackingViewName="publish_loading_dialog" disableClose>
			<div className="flex w-[462px] flex-col space-y-3">
				<div className="flex flex-row items-center justify-between">
					<div className="text-md font-bold">
						{t("publishDialog.exportingAndPublishing")}
					</div>
				</div>

				<ProgressBar max={total} value={totalProgress} />
				<div className="text-xs">{message()}</div>
				<div className="text-xs">{t("publishDialog.doNotLeaveApp")}</div>

				<div className="self-end pt-3">
					<Button
						trackingLabel="cancel_publish"
						aria-label={t("publishDialog.ariaCancelPublish")}
						onClick={cancel}
						disabled={isCanceling}
					>
						{t("common:action.cancel")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
